// janus-engine/ability/statuses/status-expiry-detonation.ts — DELAYED DETONATION (layers minted 2026-10-07 for Geno's Psion v12 PDF —
// Pyrokinetic L7 menta "Detonation Mark": "Mark a creature with concentrated heat. At the end of its next turn, the mark detonates, dealing
// 7d6 Fire damage to it and 3d6 Fire damage to other creatures within 10ft."). Browser rule — R-176.
//
//   damage_on_status_expiry             (int_0_1) — on the item that rides the target as its own status (item-as-status)
//   damage_on_status_expiry_splash_ft   (int)     — other creatures within this many feet of the holder (rim distance) also take…
//   damage_on_status_expiry_splash_dice / _die (int) — …these dice, of the same damage type
//
// RULE: when the status EXPIRES NATURALLY at the end of its holder's turn (the effect_ends_on_target_end_of_turn removal — family
// target_end), it deals the item's own dice / die / mod of its damage_type to the holder, then the splash dice (no mod) to every OTHER living,
// public (encounter_creature_private 0) roster creature within the splash radius, in encounter_creature_id order. The status caster deals the damage. It fires once:
// the row is gone after it. A row removed EARLY (dispel, a cleanse, its caster's concentration ending, death …) never detonates, and neither
// does a duration running out at another boundary. The item should carry no roll_type (No Roll): the cast itself deals nothing, it applies
// the mark (apply_ability_effect_to_target via effect_ends_on_target_end_of_turn).
//
// Browser seams: statuses/expiry.ts (the definition's chassis layers stay plain, the removal is flagged `detonates`, the boundary gates
// 'expiry-detonation' — a TICK gate, served by turns/ticks.ts), turns/ticks.ts endTargetTicks → detonateExpiredStatus below.
// Ticks preflight the effect and GM-log unported work while independent turn effects continue.
import { type JanusCtx, type Row, HandBack, activeStatuses, ecId, ecLean, handBack, itemField, record, upper } from '../context.js';
import { damageTypeRoll } from '../rolls/damage-type-roll.js';
import { runTriggerFamily, triggerFamilyPreDraw } from '../trigger-executor.js';
import { carrierNamesRollType } from './forced-roll-extreme.js';
import { carrierHasTrigger } from './status-trigger-gate.js';
import { removeStoredStatus } from './status-effect-removal-helpers.js';
import { pageDistanceFt } from '../../reactions/page-trigger-readers.js';
import { TriggerInputUnavailable, heldStatusDefinition } from '../../reactions/trigger-dispatch.js';
import { DAMAGE_ON_STATUS_EXPIRY, DETONATION_LAYERS, DETONATION_SPLASH_DICE, DETONATION_SPLASH_DIE, DETONATION_SPLASH_FT, statusCasterId } from '../../statuses/expiry.js';
export { DAMAGE_ON_STATUS_EXPIRY, DETONATION_LAYERS, DETONATION_SPLASH_DICE, DETONATION_SPLASH_DIE, DETONATION_SPLASH_FT };
const DOT_ROLL = '77A044B7-7041-CA96-3C63-BB718B15A364';
const DAMAGE_ROLL = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';

const intOf = (value: unknown): number => { let v = value; while (Array.isArray(v)) v = v[0]; const n = Number(v); return Number.isFinite(n) ? Math.trunc(n) : 0; };
const truthy = (value: unknown): boolean => { let v = value; while (Array.isArray(v)) v = v[0]; return v !== undefined && v !== null && v !== '' && v !== 0 && v !== '0' && v !== false; };

/** Does this definition detonate when it expires? */
export function statusDetonates(read: { value: (row: Row, key: string) => unknown }, definition: Row | null): boolean {
  return !!definition && truthy(read.value(definition, DAMAGE_ON_STATUS_EXPIRY));
}

/** Damage-only fragment: no cast status, placement, movement or riders. Null dice / die use the item's own dice and mod. */
function fragment(ctx: JanusCtx, definition: Row, dice: number | null, die: number | null): Row {
  const layers: Row = { dice: dice ?? ctx.read.value(definition, 'dice'), die: die ?? ctx.read.value(definition, 'die'),
    damage_type: JSON.parse(JSON.stringify(record(definition.item_layer_types)?.damage_type ?? ctx.read.value(definition, 'damage_type'))) };
  if (dice === null && ctx.read.value(definition, 'mod') != null) layers.mod = ctx.read.value(definition, 'mod');
  const copy: Row = { item_layer_types: layers };
  for (const key of ['items', 'item_id', 'item_name', 'item_type']) if (key in definition) copy[key] = JSON.parse(JSON.stringify(definition[key]));
  return copy;
}

const HINT_AXES = ['is_rolled_at_max', 'is_rolled_at_min', 'roll_result_is_halved', 'roll_result_is_doubled'] as const;
const DAMAGE_TYPES = new Set([DAMAGE_ROLL]);
const HINT_STATUS_LAYERS = new Set(['triggers_when_caster_makes_roll_type', 'triggers_on_next_roll_type',
  ...HINT_AXES.flatMap(axis => ['roll_type_' + axis, 'next_roll_type_' + axis]),
  'range', 'duration_int', 'duration_type', 'ability_charges', 'ability_resets_long_rest', 'ability_resets_short_rest', 'unenchantable', 'cost_gp']);

/** The detonation of `status` (already removed from its holder by the expiry) — the holder's own damage, then the splash. */
export function detonateExpiredStatus(ctx: JanusCtx, holder: Row, status: Row, definition: Row, abilityId: string): void {
  const priorExtreme = ctx.castForcedRollExtreme;
  ctx.castForcedRollExtreme = {};
  try { detonate(ctx, holder, status, definition, abilityId); }
  catch (error) {
    if (!(error instanceof HandBack)) throw error;
    const caster = ctx.members.get(statusCasterId(status)) ?? holder;
    const name = itemField(record(caster.player_characters), 'player_name') ?? ecLean(caster, 'encounter_creature_name')
      ?? itemField(caster, 'item_name') ?? 'Creature';
    handBack(`${name}: detonation: ${error.reason}`);
  } finally { ctx.castForcedRollExtreme = priorExtreme; }
}

function detonate(ctx: JanusCtx, holder: Row, status: Row, definition: Row, abilityId: string): void {
  if (!statusDetonates(ctx.read, definition)) return;
  if (upper(ctx.read.value(definition, 'roll_type')) === DOT_ROLL) handBack('turn-tick:detonation-dot-host');
  if (Number(status.status_forced_roll_extreme ?? 0) !== 0) handBack('turn-tick:detonation-forced-extreme');
  if (!ctx.read.has(definition, 'damage_type')) return; // nothing typed to deal
  const casterId = statusCasterId(status) || ecId(holder);
  const caster = ctx.members.get(casterId);
  if (!caster) handBack('turn-tick:detonation-caster-not-held');
  const input = (target: Row) => ({ casterId, targetIds: [ecId(target)], abilityId, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
    useAbilityVariable1: 'damage_only', useAbilityVariable2: null });
  // One roll-trigger frame for this detonation, before its damage fragments. The shared executor owns carrier/status order,
  // live gates, consent and spends. A nested trigger frame never starts another caster-roll event.
  const hints = new Set<string>();
  const mergeHints = (ability: Row): void => {
    for (const axis of HINT_AXES) if (['roll_type_', 'next_roll_type_'].some(prefix =>
      carrierNamesRollType(ctx, ability, prefix + axis, DAMAGE_TYPES))) hints.add('roll_type_' + axis);
  };
  if (!ctx.trigger) {
    const event = { trigger: 'triggers_when_caster_makes_roll_type', rollType: DAMAGE_ROLL };
    if (carrierHasTrigger(ctx, caster!, event.trigger, 'turn-tick:detonation', DAMAGE_ROLL)) {
      triggerFamilyPreDraw(ctx, caster!, event, undefined, holder);
      for (const { ability } of runTriggerFamily(ctx, event, caster!, holder, [caster!], false)) mergeHints(ability);
    }
    // A next-roll hint granted by the dispatch is consumed by this event, including its shared loss/connection tails.
    for (const next of activeStatuses(ctx, caster!)) {
      const ability = heldStatusDefinition({ definition: ctx.read.definition, itemField }, next, caster!);
      if (!ability) handBack('turn-tick:detonation-next-roll-definition-not-held');
      if (!carrierNamesRollType(ctx, ability!, 'triggers_on_next_roll_type', DAMAGE_TYPES)) continue;
      const payload = Object.keys(record(ability!.item_layer_types) ?? {}).find(key => !HINT_STATUS_LAYERS.has(key) && !key.startsWith('requires_'));
      if (payload) handBack(`turn-tick:detonation-next-roll-payload:${payload}`);
      mergeHints(ability!);
      removeStoredStatus(ctx, caster!, next, 'single');
    }
  }
  const strike = (target: Row, damage: Row): void => {
    if (hints.has('roll_type_roll_result_is_doubled')) record(damage.item_layer_types)!.roll_type_roll_result_is_doubled = [DAMAGE_ROLL];
    const previous = ctx.read, trigger = ctx.trigger;
    // Hints belong only to these fragments, not the caster's saved/compiled state or nested damage-taken reactions.
    ctx.read = { ...previous, entries: (row, key) => row === caster && ctx.trigger === trigger && hints.has(key)
      ? [...previous.entries(row, key), DAMAGE_ROLL] : previous.entries(row, key) };
    try { damageTypeRoll(ctx, input(target), damage, caster!, target); } finally { ctx.read = previous; }
  };
  if (ctx.read.has(definition, 'dice') && ctx.read.has(definition, 'die') && intOf(ctx.read.value(definition, 'dice')) > 0)
    strike(holder, fragment(ctx, definition, null, null));
  const radius = intOf(ctx.read.value(definition, DETONATION_SPLASH_FT));
  const dice = intOf(ctx.read.value(definition, DETONATION_SPLASH_DICE));
  const die = intOf(ctx.read.value(definition, DETONATION_SPLASH_DIE));
  if (radius <= 0 || dice <= 0 || die <= 0) return;
  const splash = fragment(ctx, definition, dice, die);
  const host = { value: (row: Row | null, key: string) => ctx.read.value(row, key), entries: (row: Row | null, key: string) => ctx.read.entries(row, key) };
  const holderId = ecId(holder);
  const others = [...ctx.members.values()].filter((row) => {
    const id = ecId(row);
    return id && id !== holderId && Number(ecLean(row, 'encounter_creature_dead') ?? 0) !== 1
      // Preserve the detonation roster filter (0 public / 1 private / 2 secret).
      && Number(ecLean(row, 'encounter_creature_deleted') ?? 0) !== 1 && Number(ecLean(row, 'encounter_creature_private') ?? 0) === 0;
  }).sort((a, b) => (ecId(a) < ecId(b) ? -1 : ecId(a) > ecId(b) ? 1 : 0));
  for (const other of others) {
    let distance: number | null;
    try { distance = pageDistanceFt(host, holder, other); }
    catch (error) { if (error instanceof TriggerInputUnavailable) handBack(`turn-tick:detonation-${error.message}`); throw error; }
    if (distance === null) handBack('turn-tick:detonation-distance-unknown');
    if (distance! <= radius) strike(other, splash);
  }
}
